import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { playMessageDing, unlockAudio, isSoundEnabled, setSoundEnabled } from '../../lib/notifySound';
import { notifyIncomingMessage } from '../../lib/tabAlert';
import { ensurePushSubscription, isPushSupported } from '../../lib/pushClient';
import { setAppBadge, clearAppBadge } from '../../lib/appBadge';
import {
  Send, Paperclip, Building2, MessageSquare,
  Clock, Loader2, FileText, Download, ClipboardList, Check, X,
  Volume2, VolumeX, Bell, BellOff, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// QCM banner shown above the chat when a daily report is pending
const QcmBanner = ({ pending, onAnswered }) => {
  const { t } = useTranslation();
  const [num, setNum] = useState('');
  const [patients, setPatients] = useState(['']);
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [qcmIntro, setQcmIntro] = useState('');

  // Load admin-defined intro text (best effort)
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/qcm-text`)
      .then(r => { if (mounted) setQcmIntro(r.data?.text || ''); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!pending || pending.length === 0) return null;
  const r = pending[0];
  const isZeroCheck = r.stage === 'zero_check' && r.status === 'pending';

  const updateNum = (v) => {
    const n = parseInt(v || '0', 10);
    setNum(v);
    if (!Number.isNaN(n) && n > 0 && n <= 30) {
      setPatients(prev => {
        const arr = prev.slice(0, n);
        while (arr.length < n) arr.push('');
        return arr;
      });
    } else if (v === '' || n === 0) {
      setPatients(['']);
    }
  };

  const submitAnswer = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/daily-reports/${r.id}/answer`, payload);
      toast.success(t('client.qcmSent'));
      onAnswered();
    } catch {
      toast.error(t('client.qcmError'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitNumeric = (e) => {
    e.preventDefault();
    const n = parseInt(num || '0', 10);
    if (Number.isNaN(n) || n < 0) { toast.error(t('client.qcmInvalidNumber')); return; }
    submitAnswer({
      num_empreintes: n,
      patient_names: patients.map(p => p.trim()).filter(Boolean),
      free_text: freeText.trim(),
    });
  };

  return (
    <div className="border-b border-amber-300 bg-amber-50 px-3 sm:px-4 py-3" data-testid="qcm-banner">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start gap-2 mb-2">
          <ClipboardList className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm text-amber-900">
              {t('client.qcmTitle')} — {r.report_date}
              {r.stage === 'relance' && ` ${t('client.qcmRelance')}`}
            </p>
            <p className="text-xs text-amber-800">{qcmIntro}</p>
          </div>
        </div>

        {isZeroCheck ? (
          <div className="space-y-2">
            <p className="text-sm text-amber-900">{t('client.qcmZeroQuestion')}</p>
            <div className="flex gap-2">
              <Button size="sm" disabled={submitting} onClick={() => submitAnswer({ zero_confirmed: true })} data-testid="qcm-zero-yes">
                <Check className="h-4 w-4 mr-1" />{t('client.qcmZeroYes')}
              </Button>
              <Button size="sm" variant="outline" disabled={submitting} onClick={() => submitAnswer({ zero_confirmed: false })} data-testid="qcm-zero-no">
                <X className="h-4 w-4 mr-1" />{t('common.no')}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitNumeric} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('client.qcmNumLabel')}</Label>
                <Input type="number" min="0" max="30" value={num} onChange={(e) => updateNum(e.target.value)} required className="bg-white" data-testid="qcm-num-input" />
              </div>
            </div>
            {parseInt(num || '0', 10) > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">{t('client.qcmPatientsLabel')}</Label>
                <div className="space-y-1">
                  {patients.map((p, i) => (
                    <Input
                      key={`qcm-patient-slot-${i}`}
                      value={p}
                      onChange={(e) => setPatients(prev => prev.map((v, idx) => idx === i ? e.target.value : v))}
                      placeholder={t('client.qcmPatientPlaceholder', { n: i + 1 })}
                      className="bg-white"
                      data-testid={`qcm-patient-${i}`}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">{t('client.qcmCommentLabel')}</Label>
              <Textarea rows={2} value={freeText} onChange={(e) => setFreeText(e.target.value)} className="bg-white text-sm" data-testid="qcm-free-text" />
            </div>
            <Button type="submit" size="sm" disabled={submitting} data-testid="qcm-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {t('client.qcmSubmit')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

const Chat = () => {
  const { t, i18n } = useTranslation();
  const { user, heartbeat } = useAuth();
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [labOnline, setLabOnline] = useState(false);
  const [pendingQcm, setPendingQcm] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [pushEnabled, setPushEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const heartbeatRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingThrottleRef = useRef(0);
  const lastIncomingIdRef = useRef(null);
  const initialLoadRef = useRef(true);

  // Derived: selected channel (shape compatible with the previous singular state)
  const channel = selectedChannelId
    ? (() => {
        const c = channels.find(x => x.channel_id === selectedChannelId);
        return c ? { id: c.channel_id, lab_name: c.lab_name, lab_id: c.lab_id } : null;
      })()
    : null;

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundEnabled(next);
    if (next) { unlockAudio(); playMessageDing(); }
  };

  const enablePush = async () => {
    try {
      const result = await ensurePushSubscription();
      if (result === 'ok') {
        setPushEnabled(true);
        toast.success(t('notifications.enabled'));
      } else if (result === 'denied') {
        toast.error(t('notifications.blocked'));
      } else {
        toast.error(t('notifications.unsupported'));
      }
    } catch {
      toast.error(t('notifications.blocked'));
    }
  };

  // Auto-attempt push subscription on first user gesture if not yet granted.
  useEffect(() => {
    if (!isPushSupported()) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;  // already granted or denied
    const handler = () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      ensurePushSubscription().then(r => { if (r === 'ok') setPushEnabled(true); });
    };
    window.addEventListener('click', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, []);

  const downloadInstallGuide = async () => {
    try {
      const lang = (i18n.language || 'fr').slice(0, 2);
      const r = await axios.get(`${API}/guide/mobile-install.pdf`, {
        params: { lang },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'JPD-Link_installation_mobile.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error(t('client.sendError'));
    }
  };

  const fetchPendingQcm = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/client/daily-reports/pending`);
      setPendingQcm(r.data || []);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[Chat]', e?.message);
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/client/channels`);
      const list = r.data || [];
      setChannels(list);
      // Auto-select first one when none is selected yet
      setSelectedChannelId(prev => {
        if (prev && list.some(c => c.channel_id === prev)) return prev;
        return list[0]?.channel_id || null;
      });
      // Aggregate badge across all labs
      const total = list.reduce((s, c) => s + (c.unread_count || 0), 0);
      setAppBadge(total);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[Chat]', e?.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchChannels(); fetchPendingQcm(); heartbeat(); return () => { clearInterval(pollRef.current); clearInterval(heartbeatRef.current); clearAppBadge(); }; }, [fetchChannels, fetchPendingQcm, heartbeat]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);
  const fetchMessages = useCallback(async () => {
    if (!channel?.id) return;
    try {
      const r = await axios.get(`${API}/messages/${channel.id}`);
      const msgs = r.data;
      // Detect newly arrived inbound messages and ring a chime
      const lastInbound = [...msgs].reverse().find(m => m.direction === 'lab_to_client');
      if (lastInbound) {
        if (!initialLoadRef.current && lastInbound.id !== lastIncomingIdRef.current) {
          playMessageDing();
          notifyIncomingMessage({
            title: lastInbound.sender_name || channel?.lab_name || t('client.lab'),
            body: (lastInbound.text || '').slice(0, 140),
            tag: `channel-${channel?.id}`,
          });
        }
        lastIncomingIdRef.current = lastInbound.id;
      }
      initialLoadRef.current = false;
      setMessages(msgs);
      // App-icon badge = unread messages from lab
      const unread = msgs.filter(m => m.direction === 'lab_to_client' && !m.read_at).length;
      setAppBadge(unread);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[Chat]', e?.message);
    }
  }, [channel?.id, channel?.lab_name, t]);
  const fetchPresence = useCallback(async () => { if (!channel?.id) return; try { const r = await axios.get(`${API}/presence/channel/${channel.id}`); setLabOnline(r.data.lab?.online || false); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[Chat] presence:', e?.message); } }, [channel?.id]);
  const fetchTyping = useCallback(async () => { if (!channel?.id) return; try { const r = await axios.get(`${API}/typing/${channel.id}`); setTypingUsers(r.data || []); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[Chat] typing:', e?.message); } }, [channel?.id]);
  const markRead = useCallback(async () => { if (!channel?.id) return; try { await axios.post(`${API}/channels/${channel.id}/mark-read`); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[Chat] markRead:', e?.message); } }, [channel?.id]);

  // Channel-scoped polling. Restarts whenever channel?.id or any of the
  // stable useCallback-wrapped fetchers change identity.
  useEffect(() => {
    if (channel?.id) {
      // Reset incoming-tracking when switching labs
      lastIncomingIdRef.current = null;
      initialLoadRef.current = true;
      setMessages([]);
      fetchMessages(); fetchPresence(); fetchTyping(); markRead();
      pollRef.current = setInterval(() => { fetchMessages(); fetchPresence(); fetchPendingQcm(); fetchTyping(); markRead(); fetchChannels(); }, 3000);
      heartbeatRef.current = setInterval(heartbeat, 60000);
      heartbeat();
    }
    return () => { clearInterval(pollRef.current); clearInterval(heartbeatRef.current); };
  }, [channel?.id, fetchMessages, fetchPresence, fetchTyping, markRead, fetchChannels, fetchPendingQcm, heartbeat]);

  const sendTypingPing = () => {
    if (!channel?.id) return;
    const now = Date.now();
    if (now - typingThrottleRef.current < 2000) return;
    typingThrottleRef.current = now;
    axios.post(`${API}/typing`, { channel_id: channel.id }).catch(() => {});
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !channel?.id) return;
    setSending(true);
    try { await axios.post(`${API}/messages`, { channel_id: channel.id, text: newMessage.trim() }); setNewMessage(''); fetchMessages(); }
    catch { toast.error(t('client.sendError')); }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !channel?.id) return;
    if (file.size > 50 * 1024 * 1024) { toast.error(t('client.fileTooLarge')); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await axios.post(`${API}/messages`, { channel_id: channel.id, text: `[Fichier] ${file.name}`, media_url: up.data.file_id, media_type: file.type });
      fetchMessages(); toast.success(t('client.fileSent'));
    } catch { toast.error(t('client.fileError')); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const localeKey = (i18n.language === 'en') ? 'en-GB' : 'fr-FR';
  const formatTime = (ts) => new Date(ts).toLocaleTimeString(localeKey, { hour: '2-digit', minute: '2-digit' });
  const formatDate = (ts) => {
    const d = new Date(ts), n = new Date(), y = new Date(n); y.setDate(n.getDate()-1);
    if (d.toDateString()===n.toDateString()) return i18n.language === 'en' ? 'Today' : "Aujourd'hui";
    if (d.toDateString()===y.toDateString()) return i18n.language === 'en' ? 'Yesterday' : 'Hier';
    return d.toLocaleDateString(localeKey, { weekday: 'long', day: 'numeric', month: 'long' });
  };
  const grouped = messages.reduce((a,m) => { const d = formatDate(m.timestamp); if(!a[d]) a[d]=[]; a[d].push(m); return a; }, {});

  const getSenderLabel = (msg) => {
    if (msg.direction !== 'lab_to_client' || !msg.sender_name) return null;
    if (msg.sender_name.includes(' — ')) {
      const parts = msg.sender_name.split(' — ');
      return parts[1] || parts[0];
    }
    return null; // Don't show for main lab account (no distinction needed)
  };

  const renderMedia = (msg) => {
    if (!msg.media_url) return null;
    // Browser sends the httpOnly session cookie automatically (same-origin).
    const fileUrl = `${API}/files/${msg.media_url}`;
    if (msg.media_type?.startsWith('image/')) return <img src={fileUrl} alt="" className="max-w-[200px] sm:max-w-[280px] rounded-lg mt-2 cursor-pointer" onClick={() => window.open(fileUrl, '_blank')} />;
    if (msg.media_type?.startsWith('video/')) return <video src={fileUrl} controls className="max-w-[200px] sm:max-w-[300px] rounded-lg mt-2" />;
    if (msg.media_type === 'application/pdf') return <a href={fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-2 p-2 bg-white/10 rounded-lg hover:bg-white/20"><FileText className="h-5 w-5" /><span className="text-sm underline">Ouvrir le PDF</span></a>;
    return <a href={fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-2 p-2 bg-white/10 rounded-lg"><Download className="h-4 w-4" /><span className="text-sm underline">Télécharger</span></a>;
  };

  if (loading) return <div className="chat-viewport flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="chat-viewport flex flex-col bg-background" data-testid="client-chat" onClickCapture={unlockAudio} onKeyDownCapture={unlockAudio}>
      <header className="h-14 border-b border-border bg-background/95 backdrop-blur flex items-center justify-between px-3 sm:px-6 shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src="https://customer-assets.emergentagent.com/job_dental-chat-5/artifacts/vbzuvp2d_1000265186.jpg" alt="JPD" className="w-8 h-8 sm:w-10 sm:h-10 object-contain shrink-0" />
          <div className="min-w-0">
            <h1 className="font-bold text-sm sm:text-base truncate">Jeff P. Dental</h1>
            <p className="text-xs text-muted-foreground truncate">{user?.name}</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => { window.location.href = '/orders'; }} title="Mes commandes" data-testid="nav-orders-btn">
          <ClipboardList className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleSound} title={soundOn ? 'Couper le son' : 'Activer le son'} data-testid="sound-toggle">
          {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </Button>
        {isPushSupported() && (
          <Button
            variant="ghost"
            size="icon"
            onClick={enablePush}
            title={pushEnabled ? t('notifications.enabled') : t('notifications.enable')}
            data-testid="push-toggle"
          >
            {pushEnabled ? <Bell className="h-4 w-4 text-green-600" /> : <BellOff className="h-4 w-4" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={downloadInstallGuide}
          title={t('guide.downloadTitle')}
          data-testid="download-install-guide"
        >
          <Smartphone className="h-4 w-4" />
        </Button>
        <LanguageSwitcher langs={['fr', 'en']} className="hidden sm:flex" />
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Desktop sidebar — list of labs */}
        <aside className="w-64 border-r border-border bg-muted/30 hidden lg:flex flex-col">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('client.lab')}{channels.length > 1 ? ` (${channels.length})` : ''}</p>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1" data-testid="client-labs-list">
              {channels.map(c => {
                const isSel = c.channel_id === selectedChannelId;
                return (
                  <button
                    key={c.channel_id}
                    onClick={() => setSelectedChannelId(c.channel_id)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3",
                      isSel ? "bg-primary text-primary-foreground" : "hover:bg-background"
                    )}
                    data-testid={`client-lab-item-${c.lab_id}`}
                  >
                    <div className="relative shrink-0">
                      <div className={cn("w-9 h-9 rounded-full flex items-center justify-center", isSel ? "bg-primary-foreground/20" : "bg-primary/10")}>
                        <Building2 className={cn("h-5 w-5", isSel ? "text-primary-foreground" : "text-primary")} />
                      </div>
                      {isSel && labOnline && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background" data-testid="lab-online-dot" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{c.lab_name}</p>
                      {c.last_message ? (
                        <p className={cn("text-xs truncate", isSel ? "text-primary-foreground/70" : "text-muted-foreground")}>{c.last_message}</p>
                      ) : (
                        <p className={cn("text-xs italic", isSel ? "text-primary-foreground/70" : "text-muted-foreground")}>{t('client.startConversation')}</p>
                      )}
                    </div>
                    {c.unread_count > 0 && !isSel && (
                      <span className="ml-1 shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-xs font-bold" data-testid={`unread-${c.lab_id}`}>{c.unread_count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile horizontal lab selector — visible only when 2+ labs */}
          {channels.length > 1 && (
            <div className="lg:hidden border-b border-border bg-muted/40 overflow-x-auto" data-testid="client-labs-mobile">
              <div className="flex gap-1 p-2 min-w-max">
                {channels.map(c => {
                  const isSel = c.channel_id === selectedChannelId;
                  return (
                    <button
                      key={c.channel_id}
                      onClick={() => setSelectedChannelId(c.channel_id)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1.5",
                        isSel ? "bg-primary text-primary-foreground" : "bg-background border border-border"
                      )}
                      data-testid={`client-lab-tab-${c.lab_id}`}
                    >
                      <Building2 className="h-3.5 w-3.5" />
                      {c.lab_name}
                      {c.unread_count > 0 && !isSel && (
                        <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold">{c.unread_count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mobile lab status bar (single lab info) */}
          {channel && (
            <div className="lg:hidden border-b border-border px-3 py-2 flex items-center gap-2">
              <div className="relative">
                <Building2 className="h-4 w-4 text-primary" />
                {labOnline && <div className="absolute -top-0.5 -right-1 w-2 h-2 bg-green-500 rounded-full" />}
              </div>
              <span className="text-sm font-medium">{channel.lab_name}</span>
              <span className={cn("text-xs", labOnline ? "text-green-600" : "text-muted-foreground")}>{labOnline ? t('common.online') : t('common.offline')}</span>
            </div>
          )}

          {!channel ? (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div><MessageSquare className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" /><h2 className="text-lg font-bold mb-2">{t('client.no_channel')}</h2><p className="text-sm text-muted-foreground">{t('client.no_channel_hint')}</p></div>
            </div>
          ) : (
            <>
              <QcmBanner pending={pendingQcm} onAnswered={() => { fetchPendingQcm(); fetchMessages(); }} />
              <ScrollArea className="flex-1 p-3 sm:p-4">
                <div className="max-w-3xl mx-auto space-y-4">
                  {Object.entries(grouped).map(([date, msgs]) => (
                    <div key={date}>
                      <div className="flex items-center justify-center mb-3"><span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">{date}</span></div>
                      <div className="space-y-2">
                        {msgs.map((msg) => {
                          const senderLabel = getSenderLabel(msg);
                          return (
                            <div key={msg.id} className={cn("flex", msg.direction === 'client_to_lab' ? 'justify-end' : 'justify-start')}>
                              <div className={cn("max-w-[85%] sm:max-w-[75%] px-3 py-2 sm:px-4 sm:py-2.5 animate-slide-up", msg.direction === 'client_to_lab' ? 'chat-bubble-client' : 'chat-bubble-lab')}>
                                {senderLabel && <p className="text-xs font-semibold mb-1 opacity-80" data-testid="sender-label">{senderLabel}</p>}
                                {(!msg.media_url || !msg.text.startsWith('[Fichier]')) && <p className="text-base sm:text-base whitespace-pre-wrap break-words leading-relaxed">{msg.text}</p>}
                                {msg.media_url && msg.text.startsWith('[Fichier]') && <p className="text-base font-medium">{msg.text.replace('[Fichier] ', '')}</p>}
                                {renderMedia(msg)}
                                <p className={cn("text-xs mt-1 flex items-center gap-1", msg.direction === 'client_to_lab' ? 'text-primary-foreground/70 justify-end' : 'text-muted-foreground')}>
                                  <span>{formatTime(msg.timestamp)}</span>
                                  {msg.direction === 'client_to_lab' && channels.length > 1 && channel?.lab_name && (
                                    <span className="opacity-80" data-testid="dest-lab-label"> · → {channel.lab_name}</span>
                                  )}
                                  {msg.direction === 'client_to_lab' && msg.read_at && (
                                    <span className="lowercase" data-testid="read-indicator">· {t('common.read')}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {messages.length === 0 && <div className="text-center py-12 text-muted-foreground"><MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>{t('client.noMessages')}</p><p className="text-sm">{t('client.startConversation')}</p></div>}
                  {typingUsers.length > 0 && (
                    <div className="flex justify-start" data-testid="typing-indicator">
                      <div className="px-3 py-2 rounded-2xl bg-muted text-xs text-muted-foreground italic">
                        {typingUsers[0].name || t('client.lab')} {t('common.isTyping')}
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              <div className="border-t border-border bg-background p-3 sm:p-4 pb-[env(safe-area-inset-bottom,16px)]">
                <form onSubmit={handleSend} className="max-w-3xl mx-auto flex items-center gap-2">
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept="image/*,video/*,.pdf,.doc,.docx" />
                  <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Joindre un fichier (max 50 MB)" data-testid="attach-file-btn">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
                  </Button>
                  <Input value={newMessage} onChange={(e) => { setNewMessage(e.target.value); sendTypingPing(); }} onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300); }} placeholder={t('client.writePlaceholder')} className="flex-1 text-base sm:text-base h-12 sm:h-11 no-zoom-input" disabled={sending} data-testid="message-input" />
                  <Button type="submit" size="icon" disabled={!newMessage.trim() || sending} className="bg-accent hover:bg-accent/90 shrink-0 h-12 w-12 sm:h-11 sm:w-11" data-testid="send-message-button">
                    {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </Button>
                </form>
                <p className="text-[10px] text-muted-foreground text-center mt-1">{t('client.attachHint')}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
